import {
    DirectionalHint,
    IconButton,
    IContextualMenuItem,
    IContextualMenuProps,
    nuOrderTheme as theme,
} from "@nuorder/components";
import { DATATEST } from "constants/integration";
import { merge, $ } from "glamor";
import { formatMessage } from "lib/globalize-util";
import React from "react";
import { connect } from "react-redux";
import { IStore } from "reducers";
import { Attribute } from "reducers/dimensions-panel/dimensions-panel";
import { Chip } from "@material-ui/core";
import { Check as CheckIcon } from "@material-ui/icons";
import { ToggleAttributeLabel } from "actions/tool-panel/draft/types";
import { toggleAttributeLabel } from "actions/tool-panel/draft/actions";

const menuStyles = merge(
    $(".menuButtonOption", {
        height: "36px",
        lineHeight: "36px",
        width: "100%",
        padding: "0px 8px 0px 4px",
        outline: "transparent",
        position: "relative",
        fontSize: "14px",
        fontWeight: 400,
        color: "rgb(50, 49, 48)",
        backgroundColor: "transparent",
        border: "none",
        textAlign: "left",
        display: "block",
        cursor: "pointer",
    }),
    $(".headerMenuButtonOption", {
        height: "36px",
        lineHeight: "36px",
        width: "100%",
        padding: "0px 8px 0px 4px",
        outline: "transparent",
        position: "relative",
        fontSize: "14px",
        fontWeight: 400,
        color: "rgb(50, 49, 48)",
        backgroundColor: "transparent",
        border: "none",
        borderBottom: `1px solid ${theme.palette.greyBase}`,
        textAlign: "left",
        display: "block",
        cursor: "default",
    }),
    $(".menuButtonOption:hover", {
        backgroundColor: "rgb(237, 235, 233)",
    }),
    $(" .menuOption", {
        lineHeight: "36px",
        color: "rgb(32, 31, 30)",
        margin: "0px 4px",
    }),
    $(" .dimensionChip", {
        backgroundColor: theme.palette.blue,
        color: theme.palette.white,
        borderRadius: "5px",
        height: "20px",
        fontWeight: "700",
        marginLeft: "10px",
    })
);

const muiStyles = {
    selectedMenuItem: {
        color: theme.palette.themePrimary,
        marginLeft: "2px",
    },
};

const makeMenuProps = (attributes: Attribute[], onClick: ToggleAttributeLabel): IContextualMenuProps => {
    const headerRow: IContextualMenuItem = {
        /* Show door labels - Header Row - this is a no-op row  */
        key: "show-door-labels-header-row",
        text: formatMessage("Show door labels"),
        onRender: function render(item: IContextualMenuItem) {
            return (
                <>
                    <button
                        {...menuStyles}
                        className="headerMenuButtonOption"
                        data-test={DATATEST.ASSORTMENTS.GROUPING.ATTRIBUTES.SHOW_DOORS_LABEL_ITEM}
                    >
                        <span className="menuOption">{item.text}</span>
                    </button>
                </>
            );
        },
    };
    return {
        directionalHint: DirectionalHint.leftTopEdge,
        gapSpace: 0,
        beakWidth: 20,
        directionalHintFixed: false,
        items: [headerRow].concat(
            attributes.map((attribute, index) => makeAttributeMenuItem(attribute, onClick, index))
        ),
    };
};

type AttributeMenuItem = {
    data: Attribute;
};

const makeAttributeMenuItem = (
    attribute: Attribute,
    onClick: ToggleAttributeLabel,
    attributeIndex: number
): IContextualMenuItem => ({
    key: attribute.id,
    text: attribute.name,
    data: attribute,
    onClick: () => onClick({ attributeIndex }),
    onRender: function render(
        item: AttributeMenuItem & IContextualMenuItem,
        dismissMenu: (ev?: any, dismissAll?: boolean) => void
    ) {
        return (
            <>
                <button
                    {...menuStyles}
                    className="menuButtonOption"
                    data-test={`${DATATEST.ASSORTMENTS.GROUPING.ATTRIBUTES.SET_DOOR_GROUPINGS_ITEM}-${attribute.id}`}
                    onClick={() => {
                        item.onClick();
                        dismissMenu(null, true);
                    }}
                >
                    <span className="menuOption">{item.text}</span>
                    {item.data.isUserDefined && <Chip label={formatMessage("User")} className="dimensionChip" />}
                    {item.data.showLabelInHeader && <CheckIcon style={muiStyles.selectedMenuItem} />}
                </button>
            </>
        );
    },
});

type ReduxProps = {
    attributes: Attribute[];
    toggleAttributeLabelAction: ToggleAttributeLabel;
};

const ShowDoorLabelIcon = ({ attributes, toggleAttributeLabelAction }: ReduxProps) => {
    const menuProps = makeMenuProps(attributes, toggleAttributeLabelAction);
    return (
        <div>
            <IconButton
                role="button"
                className="item-tag"
                aria-haspopup
                title={formatMessage("Show doors label")}
                menuIconProps={{
                    iconName: "tag",
                }}
                menuProps={menuProps}
                data-test={DATATEST.ASSORTMENTS.GROUPING.ATTRIBUTES.OPEN_SHOW_DOORS_LABEL}
            />
        </div>
    );
};

const mapStateToProps = (state: IStore) => ({ attributes: state.dimensionsPanel.draft.attributes });

const mapDispatchToProps = { toggleAttributeLabelAction: toggleAttributeLabel };

export const ShowDoorLabelIconContainer = connect(mapStateToProps, mapDispatchToProps)(ShowDoorLabelIcon);
